/** Typed calls, one group per API router. No UI concerns live here. */

import { api, downloadFile } from "./client";
import type {
  AuditEntry,
  BackupInfo,
  BlockedClientCreate,
  BlockedClientOut,
  BulkFlagResponse,
  CalculateRequest,
  CalculateResponse,
  ChartSeries,
  CommitResponse,
  DashboardStats,
  FormulaList,
  ImportDefaults,
  LoginResponse,
  Meta,
  OperatorCreate,
  OperatorOut,
  PageOut,
  PreviewResponse,
  RecordCreate,
  RecordFilter,
  RecordOut,
  RecordUpdate,
  StatisticsSummary,
  TableData,
  Totals,
  TradeRowResponse,
  VpsClientCreate,
  VpsClientOut,
  VpsClientUpdate,
  VpsStatus,
  VpsTierSummary,
} from "./types";

// -- auth --------------------------------------------------------------------

export const auth = {
  login: (email: string, password: string) =>
    api.postAnonymous<LoginResponse>("/auth/login", { email, password }),
  me: () => api.get<OperatorOut>("/auth/me"),
  logout: () => api.post<void>("/auth/logout"),
};

export const meta = {
  get: () => api.get<Meta>("/meta"),
};

// -- records -----------------------------------------------------------------

export interface SearchArgs {
  criteria: RecordFilter;
  page: number;
  page_size: number;
  order_by: string;
  descending: boolean;
}

export const records = {
  search: (args: SearchArgs, signal?: AbortSignal) =>
    api.post<PageOut>("/records/search", args, signal),
  totals: (criteria: RecordFilter, signal?: AbortSignal) =>
    api.post<Totals>("/records/totals", { criteria }, signal),
  get: (id: number) => api.get<RecordOut>(`/records/${id}`),
  // The record form asks Yes/No about an old Position Date before calling
  // these, so the server is told that question has been answered.
  // A blocked client is confirmed separately: the form asks first (see
  // blockedClients.check) and only then passes confirmBlocked = true.
  create: (data: RecordCreate, confirmBlocked = false) =>
    api.post<RecordOut>(
      `/records?confirm_old_position=true&confirm_blocked_client=${confirmBlocked}`,
      data,
    ),
  update: (id: number, data: RecordUpdate, confirmBlocked = false) =>
    api.put<RecordOut>(
      `/records/${id}?confirm_old_position=true&confirm_blocked_client=${confirmBlocked}`,
      data,
    ),
  remove: (id: number) => api.delete<void>(`/records/${id}`),
  distinct: (column: string) => api.get<string[]>(`/records/distinct/${column}`),
  auditTrail: (id: number) => api.get<AuditEntry[]>(`/records/${id}/audit`),

  setDone: (id: number, is_done: boolean) =>
    api.patch<RecordOut>(`/records/${id}/done`, { is_done }),
  setRejected: (id: number, is_rejected: boolean) =>
    api.patch<RecordOut>(`/records/${id}/rejected`, { is_rejected }),
  setDoneBulk: (ids: number[], is_done: boolean) =>
    api.post<BulkFlagResponse>("/records/done", { ids, is_done }),
  setRejectedBulk: (ids: number[], is_rejected: boolean) =>
    api.post<BulkFlagResponse>("/records/rejected", { ids, is_rejected }),
  removeBulk: (ids: number[]) => api.post<BulkFlagResponse>("/records/delete", { ids }),

  // "Mark every record this filter matches" — one request that resolves the
  // set server-side, rather than paging every id down to send it back.
  setDoneMatching: (criteria: RecordFilter, value: boolean) =>
    api.post<BulkFlagResponse>("/records/done-matching", { criteria, value }),
  setRejectedMatching: (criteria: RecordFilter, value: boolean) =>
    api.post<BulkFlagResponse>("/records/rejected-matching", { criteria, value }),

  export: (criteria: RecordFilter, format: "xlsx" | "csv") => {
    const stamp = new Date()
      .toISOString()
      .slice(0, 16)
      .replace(/[-:]/g, "")
      .replace("T", "_");
    return downloadFile(
      "/records/export",
      { criteria, format },
      `compensation_records_${stamp}.${format}`,
    );
  },
};

// -- operators ---------------------------------------------------------------

export const operators = {
  list: (activeOnly = false) =>
    api.get<OperatorOut[]>(`/operators?active_only=${activeOnly ? "true" : "false"}`),
  create: (data: OperatorCreate) => api.post<OperatorOut>("/operators", data),
  update: (id: number, data: OperatorCreate) => api.put<OperatorOut>(`/operators/${id}`, data),
  setActive: (id: number, active: boolean) =>
    api.patch<OperatorOut>(`/operators/${id}/active?active=${active ? "true" : "false"}`),
  setPassword: (id: number, password: string) =>
    api.patch<OperatorOut>(`/operators/${id}/password`, { password }),
  remove: (id: number) => api.delete<void>(`/operators/${id}`),
};

// -- vps tracker -------------------------------------------------------------

export const vps = {
  tiers: () => api.get<VpsTierSummary[]>("/vps/tiers"),
  due: () => api.get<VpsClientOut[]>("/vps/due"),
  list: (tier: number) => api.get<VpsClientOut[]>(`/vps?tier=${tier}`),
  listAll: () => api.get<VpsClientOut[]>("/vps"),
  create: (data: VpsClientCreate) => api.post<VpsClientOut>("/vps", data),
  update: (id: number, data: VpsClientUpdate) => api.put<VpsClientOut>(`/vps/${id}`, data),
  setStatus: (id: number, status: VpsStatus) =>
    api.patch<VpsClientOut>(`/vps/${id}/status`, { status }),
  setFree: (id: number, isFree: boolean) =>
    api.patch<VpsClientOut>(`/vps/${id}/free`, { is_free: isFree }),
  markChecked: (id: number) => api.post<VpsClientOut>(`/vps/${id}/checked`),
  remove: (id: number) => api.delete<void>(`/vps/${id}`),
  exportCredentials: (id: number, vpsNumber: string) =>
    downloadFile(`/vps/${id}/export-credentials`, undefined, `vps_${vpsNumber}_credentials.txt`),
};

// -- analytics ---------------------------------------------------------------

export const analytics = {
  stats: (criteria: RecordFilter, signal?: AbortSignal) =>
    api.post<DashboardStats>("/analytics/stats", criteria, signal),
  summary: (criteria: RecordFilter, signal?: AbortSignal) =>
    api.post<StatisticsSummary>("/analytics/summary", criteria, signal),
  charts: (
    criteria: RecordFilter,
    options: { by_day_days?: number; top_symbols_count?: number } = {},
    signal?: AbortSignal,
  ) => api.post<ChartSeries>("/analytics/charts", { criteria, ...options }, signal),
};

// -- calculations ------------------------------------------------------------

export const calculations = {
  formulas: () => api.get<FormulaList>("/calculations/formulas"),
  evaluate: (data: CalculateRequest) => api.post<CalculateResponse>("/calculations", data),
};

// -- bulk paste --------------------------------------------------------------

export interface PreviewArgs {
  text: string;
  defaults: ImportDefaults;
  mapping?: Record<string, number> | null;
  skip_header?: boolean | null;
}

export const imports = {
  preview: (args: PreviewArgs, signal?: AbortSignal) =>
    api.post<PreviewResponse>("/import/preview", args, signal),
  tradeRow: (text: string) => api.post<TradeRowResponse>("/import/trade-row", { text }),
  commit: (
    recordsToInsert: RecordCreate[],
    duplicates_skipped: number,
    errors_skipped: number,
    confirmed_old = false,
    confirmed_blocked = false,
  ) =>
    api.post<CommitResponse>("/import/commit", {
      records: recordsToInsert,
      duplicates_skipped,
      errors_skipped,
      confirmed_old,
      confirmed_blocked,
    }),
};

// -- blocked clients ---------------------------------------------------------

export const blockedClients = {
  list: () => api.get<BlockedClientOut[]>("/blocked-clients"),
  add: (data: BlockedClientCreate) => api.post<BlockedClientOut>("/blocked-clients", data),
  remove: (id: number) => api.delete<void>(`/blocked-clients/${id}`),
  /** The block entry for one client, or null when the client is not blocked. */
  check: (clientId: string) =>
    api.get<BlockedClientOut | null>(
      `/blocked-clients/check?client_id=${encodeURIComponent(clientId.trim())}`,
    ),
};

// -- settings / backups / admin ---------------------------------------------

export const settings = {
  get: <T>(key: string) => api.get<{ value: T | null }>(`/settings/${key}`),
  set: <T>(key: string, value: T) => api.put<{ value: T }>(`/settings/${key}`, { value }),
};

export const backups = {
  list: () => api.get<BackupInfo[]>("/backups"),
  create: () => api.post<BackupInfo>("/backups"),
  restore: (name: string) => api.post<BackupInfo>("/backups/restore", { name }),
};

export const admin = {
  tables: () => api.get<string[]>("/admin/tables"),
  table: (name: string, search: string | null, limit: number, signal?: AbortSignal) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (search) params.set("search", search);
    return api.get<TableData>(`/admin/tables/${encodeURIComponent(name)}?${params}`, signal);
  },
};
